import React, { useState } from 'react';
import { adminAPI } from '../services/api';
import './PasswordResetModal.css';

const EXTEND_OPTIONS = [
  { label: '1 day', hours: 24 },
  { label: '2 days', hours: 48 },
  { label: '3 days', hours: 72 },
  { label: '7 days', hours: 168 },
];

function getTokenState(passwordResetExpires) {
  if (!passwordResetExpires) return 'none';
  return new Date(passwordResetExpires) > new Date() ? 'active' : 'expired';
}

function formatExpiry(passwordResetExpires) {
  const expiry = new Date(passwordResetExpires);
  const now = new Date();
  const diffMs = expiry - now;

  if (diffMs > 0) {
    const diffH = Math.floor(diffMs / (1000 * 60 * 60));
    const diffM = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (diffH > 0) return `${diffH}h ${diffM}m remaining`;
    return `${diffM}m remaining`;
  } else {
    const agoMs = now - expiry;
    const agoH = Math.floor(agoMs / (1000 * 60 * 60));
    const agoM = Math.floor((agoMs % (1000 * 60 * 60)) / (1000 * 60));
    if (agoH > 0) return `expired ${agoH}h ${agoM}m ago`;
    return `expired ${agoM}m ago`;
  }
}

function PasswordResetModal({ user, onClose, onSuccess }) {
  const tokenState = getTokenState(user.password_reset_expires);

  // Action selection
  const defaultAction = tokenState === 'active' ? 'resend' : 'generate';
  const [selectedAction, setSelectedAction] = useState(defaultAction);
  const [extendHours, setExtendHours] = useState(24);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      if (selectedAction === 'generate') {
        await adminAPI.sendPasswordReset(user.id, { action: 'generate' });
        onSuccess(`Password reset email sent to ${user.email}`);

      } else if (selectedAction === 'extend-email') {
        await adminAPI.sendPasswordReset(user.id, { action: 'extend', extendHours, sendEmail: true });
        onSuccess(`Reset link extended by ${EXTEND_OPTIONS.find(o => o.hours === extendHours).label} and emailed to ${user.email}`);

      } else if (selectedAction === 'extend-copy') {
        const res = await adminAPI.sendPasswordReset(user.id, { action: 'extend', extendHours, sendEmail: false });
        await navigator.clipboard.writeText(res.data.resetUrl);
        const expiry = new Date(res.data.expiresAt).toLocaleString();
        onSuccess(`Reset link copied to clipboard (expires ${expiry})`);

      } else if (selectedAction === 'resend') {
        await adminAPI.sendPasswordReset(user.id, { action: 'resend' });
        onSuccess(`Password reset email re-sent to ${user.email}`);

      } else if (selectedAction === 'copy') {
        const res = await adminAPI.getResetLink(user.id);
        await navigator.clipboard.writeText(res.data.resetUrl);
        const expiry = new Date(res.data.expiresAt).toLocaleString();
        onSuccess(`Reset link copied to clipboard (expires ${expiry})`);

      } else if (selectedAction === 'generate-new') {
        await adminAPI.sendPasswordReset(user.id, { action: 'generate' });
        onSuccess(`New password reset email sent to ${user.email}`);
      }

      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred. Please try again.');
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (tokenState === 'none') {
      return (
        <>
          <p className="prm-description">
            No active reset token for <strong>{user.username}</strong>. A new reset link will be
            generated and emailed to <strong>{user.email}</strong>.
          </p>
        </>
      );
    }

    if (tokenState === 'expired') {
      return (
        <>
          <p className="prm-description">
            <strong>{user.username}</strong> has a reset token that{' '}
            <span className="prm-expired">{formatExpiry(user.password_reset_expires)}</span>.
          </p>
          <div className="prm-options">
            <label className="prm-option">
              <input
                type="radio"
                name="resetAction"
                value="generate"
                checked={selectedAction === 'generate'}
                onChange={() => setSelectedAction('generate')}
              />
              <span>Generate new token &amp; email user</span>
            </label>

            <label className="prm-option">
              <input
                type="radio"
                name="resetAction"
                value="extend-email"
                checked={selectedAction === 'extend-email'}
                onChange={() => setSelectedAction('extend-email')}
              />
              <span>Extend expiry &amp; email user</span>
            </label>

            <label className="prm-option">
              <input
                type="radio"
                name="resetAction"
                value="extend-copy"
                checked={selectedAction === 'extend-copy'}
                onChange={() => setSelectedAction('extend-copy')}
              />
              <span>Extend expiry &amp; copy link (no email)</span>
            </label>

            {(selectedAction === 'extend-email' || selectedAction === 'extend-copy') && (
              <div className="prm-extend-options">
                <span className="prm-extend-label">Extend by:</span>
                {EXTEND_OPTIONS.map((opt) => (
                  <button
                    key={opt.hours}
                    type="button"
                    className={`prm-extend-btn${extendHours === opt.hours ? ' selected' : ''}`}
                    onClick={() => setExtendHours(opt.hours)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      );
    }

    // tokenState === 'active'
    return (
      <>
        <p className="prm-description">
          <strong>{user.username}</strong> already has an active reset link (
          <span className="prm-active">{formatExpiry(user.password_reset_expires)}</span>).
        </p>
        <div className="prm-options">
          <label className="prm-option">
            <input
              type="radio"
              name="resetAction"
              value="resend"
              checked={selectedAction === 'resend'}
              onChange={() => setSelectedAction('resend')}
            />
            <span>Re-send existing link via email</span>
          </label>

          <label className="prm-option">
            <input
              type="radio"
              name="resetAction"
              value="copy"
              checked={selectedAction === 'copy'}
              onChange={() => setSelectedAction('copy')}
            />
            <span>Copy reset link to clipboard (no email)</span>
          </label>

          <label className="prm-option">
            <input
              type="radio"
              name="resetAction"
              value="generate-new"
              checked={selectedAction === 'generate-new'}
              onChange={() => setSelectedAction('generate-new')}
            />
            <span>Generate new token &amp; email user (invalidates existing)</span>
          </label>
        </div>
      </>
    );
  };

  const getConfirmLabel = () => {
    if (tokenState === 'none') return 'Generate & Email';
    const labels = {
      generate: 'Generate & Email',
      'extend-email': 'Extend & Email',
      'extend-copy': 'Extend & Copy Link',
      resend: 'Re-send Email',
      copy: 'Copy Link',
      'generate-new': 'Generate & Email',
    };
    return labels[selectedAction] || 'Confirm';
  };

  const titleMap = {
    none: 'Send Password Reset',
    expired: 'Password Reset — Expired Token',
    active: 'Password Reset — Active Token',
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container prm-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{titleMap[tokenState]}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="prm-body">
          {renderContent()}

          {error && <div className="submit-error">{error}</div>}

          <div className="form-actions">
            <button type="button" className="btn btn-cancel" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleConfirm} disabled={loading}>
              {loading ? 'Please wait…' : getConfirmLabel()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PasswordResetModal;
